import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import clsx from 'clsx';

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease';
    period: string;
  };
  trend?: 'up' | 'down';
  trendValue?: string;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  loading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title, value, change, trend, trendValue, icon, color = 'blue', loading = false,
}) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600', red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  const changeColor: Record<string, string> = { increase: 'text-green-600 bg-green-50', decrease: 'text-red-600 bg-red-50' };
  const trendColor: Record<string, string> = { up: 'text-green-600 bg-green-50', down: 'text-red-600 bg-red-50' };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-3"><div className="h-3 bg-slate-200 rounded w-20"></div><div className="h-8 w-8 bg-slate-200 rounded-lg"></div></div>
          <div className="h-6 bg-slate-200 rounded w-24 mb-2"></div>
          <div className="h-3 bg-slate-200 rounded w-16"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center justify-between mb-2 sm:mb-4">
        <h3 className="text-xs sm:text-sm font-medium text-slate-600 leading-tight">{title}</h3>
        {icon && <div className={clsx('w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0', colorClasses[color])}>{icon}</div>}
      </div>
      <div className="mb-1 sm:mb-2"><p className="text-lg sm:text-2xl font-bold text-slate-900 break-words">{value}</p></div>
      {change && (
        <div className="flex items-center">
          <div className={clsx('flex items-center px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium', changeColor[change.type])}>
            {change.type === 'increase' ? <ArrowUp className="w-3 h-3 mr-0.5" /> : <ArrowDown className="w-3 h-3 mr-0.5" />}{change.value}%
          </div>
          <span className="text-[10px] sm:text-xs text-slate-500 ml-1.5">{change.period}</span>
        </div>
      )}
      {!change && trend && trendValue && (
        <div className="flex items-center">
          <div className={clsx('flex items-center px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium', trendColor[trend])}>
            {trend === 'up' ? <ArrowUp className="w-3 h-3 mr-0.5" /> : <ArrowDown className="w-3 h-3 mr-0.5" />}
            <span className="truncate">{trendValue}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsCard;